import { ExternalLink, Search } from "lucide-react";
import { useId } from "react";
import { Button } from "../../components/primitives";
import type { WebsiteResearchState, WebsiteSuggestion } from "../../lib/types";
import { normalizeComparableUrl } from "../../lib/website";

/**
 * ORGANIZATION WEBSITE RESEARCH.
 *
 * Choosing the partner's official domain is a GOVERNED choice: pick the wrong one and every
 * downstream edit is personalized to the wrong organization. It was a row of buttons with a
 * bare confidence percentage. It is an option group now, and every card states its
 * consequence.
 *
 * THE HONESTY FIX — a 42%-class defect nobody had named:
 * when live research is unavailable, lib/website.ts fabricates candidate domains from the
 * ORGANIZATION NAME (`createWebsiteFallback` -> `confidence: index === 0 ? 0.44 : 0.36`) and
 * the panel printed "44%" and "36%" on them. Those numbers are literally hardcoded constants
 * attached to a string manipulation. No research produced them, no evidence supports them, and
 * a governance product may not print a confidence figure it invented. A fabricated percentage
 * is a fabricated percentage whether it appears on a QA gate or on a domain guess.
 *
 * So: a confidence figure is shown ONLY when a real provider returned one, and it is LABELLED
 * with that provider ("Confidence 44% · Gemini"). In fallback there is no number at all — just
 * the truth, in words: these were generated from the name and have not been verified.
 *
 * The IDLE box is gone. It was a permanent 44px hint that said "Type an organization name to
 * find likely official websites" — which is exactly what the Organization field's own help
 * line now says, at the moment it is useful.
 */

/** The local guess generator in lib/website.ts identifies itself with this provider string. */
const LOCAL_FALLBACK = "Local fallback";

const HEAD: Record<"loading" | "fallback" | "researched" | "empty", { title: string; body: string }> = {
  loading: {
    title: "Looking for the official website",
    body: ""
  },
  researched: {
    title: "Websites found",
    body: "Select the official domain. Every edit below is personalized to it."
  },
  fallback: {
    title: "Not researched",
    body: "Live research was unavailable. These domains were generated from the organization name and have not been verified. Confirm the domain before you continue."
  },
  empty: {
    title: "No confident match",
    body: "Enter the website manually, or adjust the name and search again."
  }
};

export function WebsiteResearchPanel({
  id,
  research,
  selectedUrl,
  onSelect,
  onRefresh
}: {
  id?: string;
  research: WebsiteResearchState;
  selectedUrl: string;
  onSelect: (suggestion: WebsiteSuggestion) => void;
  onRefresh: () => void;
}) {
  const name = useId();

  // Nothing to say yet. The Organization field's help line already covers it.
  if (research.status === "idle") return null;

  const loading = research.status === "loading";
  // A provider is only a provider if research actually ran. Absence of an error is not
  // evidence of a lookup.
  const provider = research.provider && research.provider !== LOCAL_FALLBACK ? research.provider : "";
  const state = loading
    ? "loading"
    : research.status === "empty" || !research.suggestions.length
      ? "empty"
      : provider
        ? "researched"
        : "fallback";
  const head = HEAD[state];

  return (
    <section
      id={id}
      className="website-research"
      aria-live="polite"
      data-testid="website-research"
      data-state={research.status}
      data-provider={provider ? "researched" : "fallback"}
    >
      <div className="website-research-head">
        <div className="website-research-copy">
          <h4 className="website-research-title">{head.title}</h4>
          <p className="website-research-body">{loading ? `Searching ${research.query}` : head.body}</p>
        </div>
        <Button variant="ghost" size="sm" data-testid="website-refresh" onClick={onRefresh} disabled={loading}>
          <Search aria-hidden="true" />
          Search again
        </Button>
      </div>

      {research.suggestions.length ? (
        <div className="website-option-list" role="radiogroup" aria-label="Official domain">
          {research.suggestions.map((suggestion) => {
            const selected = normalizeComparableUrl(selectedUrl) === normalizeComparableUrl(suggestion.url);
            return (
              <div
                className="website-option"
                data-testid="website-option"
                data-selected={selected ? "true" : "false"}
                key={`${suggestion.url}-${suggestion.title}`}
              >
                <label className="website-option-hit">
                  <input
                    type="radio"
                    className="option-input"
                    name={name}
                    data-testid="website-option-select"
                    aria-label={`Use ${suggestion.domain}`}
                    checked={selected}
                    onChange={() => onSelect(suggestion)}
                  />
                  <span className="website-option-copy">
                    {/* THE CARD IS TITLED BY THE THING THAT ACTUALLY DIFFERS. Both fallback
                        candidates carried the IDENTICAL title ("<Org> official website
                        candidate") and only the URL underneath told them apart — a chooser whose
                        two options read as the same option. The DOMAIN is the choice; it is the
                        title. The generic phrase is demoted to the description line, where it
                        belongs and where it loses nothing. */}
                    <span className="website-option-title">{suggestion.domain}</span>
                    <span className="website-option-url">{suggestion.url}</span>
                    <span className="website-option-consequence">
                      {suggestion.description ||
                        suggestion.reason ||
                        suggestion.title ||
                        "Everything in the document is personalized to this organization."}
                    </span>
                    {/* A number ONLY when a real provider produced one, and named. */}
                    {provider ? (
                      <span className="website-confidence">
                        Confidence {Math.round(suggestion.confidence * 100)}% · {provider}
                      </span>
                    ) : null}
                  </span>
                  {/* ONE AFFORDANCE PER ACT. The card offered TWO ways to do the same thing — a
                      radio at the top-left and a "Use this domain" link at the bottom-left — so
                      an operator had to work out whether they were the same control. The whole
                      card IS the radio (this is the <label>), so the second affordance is gone.
                      What remains is the STATE, and only when there is a state to report. */}
                  {selected ? (
                    <span className="website-option-state" aria-hidden="true">
                      Selected
                    </span>
                  ) : null}
                </label>
                {suggestion.sourceUrl ? (
                  <a className="website-source" href={suggestion.sourceUrl} target="_blank" rel="noreferrer">
                    Source
                    <ExternalLink aria-hidden="true" />
                  </a>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
